import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiKey, FiPlus, FiTrash2, FiCopy, FiCheck, FiAlertCircle, FiBriefcase, FiRefreshCw, FiShield, FiLink, FiEye, FiEyeOff, FiChevronDown, FiSearch } from 'react-icons/fi';
import './MCPSettingsModal.css';

// MCP Service Base URL
const MCP_BASE_URL = 'https://phone.getscreened.in/api/kg';

const MCPSettingsModal = ({ isOpen, onClose, companyId, companyEmail: propEmail, availableJDs = [] }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState('tokens');
  
  // Company info - get from props or localStorage
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  
  // Token management state
  const [tokens, setTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [newTokenExpiry, setNewTokenExpiry] = useState(365);
  const [selectedJDsForToken, setSelectedJDsForToken] = useState([]);
  const [isJDSelectorOpen, setIsJDSelectorOpen] = useState(false);
  const [jdSearchTerm, setJDSearchTerm] = useState('');
  const [createdToken, setCreatedToken] = useState(null);
  const [copiedTokenId, setCopiedTokenId] = useState(null);
  
  // Selected token for management
  const [selectedToken, setSelectedToken] = useState(null);
  const [tokenJDs, setTokenJDs] = useState([]);
  const [tokenJDsLoading, setTokenJDsLoading] = useState(false);
  
  // Service state
  const [serviceHealth, setServiceHealth] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [creatingToken, setCreatingToken] = useState(false);

  // Get company info on mount
  useEffect(() => {
    if (isOpen) {
      // Get company email from props first, then localStorage - using the correct key "CompanyEmailID"
      const email = propEmail || 
                    localStorage.getItem('CompanyEmailID') || 
                    localStorage.getItem('companyEmail') || 
                    localStorage.getItem('company_email') || '';
      
      // Get company name from CompanyProfile (same as CompanyHome.js)
      let name = 'Company';
      const storedCompanyProfile = localStorage.getItem('CompanyProfile');
      if (storedCompanyProfile) {
        try {
          const parsedProfile = JSON.parse(storedCompanyProfile);
          name = parsedProfile.companyName || 'Company';
        } catch (e) {
          console.warn('Error parsing CompanyProfile:', e);
        }
      }
      
      console.log('📧 MCP Modal - Company Email:', email);
      console.log('🏢 MCP Modal - Company Name:', name);
      
      setCompanyEmail(email);
      setCompanyName(name);
      checkServiceHealth();
      
      // Auto-select all JDs by default for easier token creation
      if (availableJDs.length > 0 && selectedJDsForToken.length === 0) {
        setSelectedJDsForToken(availableJDs.map(jd => jd.id));
      }
      
      if (email) {
        loadTokens(email);
      }
    }
  }, [isOpen, availableJDs, propEmail]);

  const checkServiceHealth = async () => {
    try {
      const response = await fetch(`${MCP_BASE_URL}/health`);
      const data = await response.json();
      setServiceHealth(data);
    } catch (error) {
      setServiceHealth({ status: 'error', message: error.message });
    }
  };

  // Load all tokens for this email
  const loadTokens = async (email) => {
    if (!email) return;
    
    setTokensLoading(true);
    try {
      // We need a token to list tokens - use the first stored one or skip
      const storedToken = localStorage.getItem('mcp_primary_token');
      if (!storedToken) {
        setTokens([]);
        return;
      }

      const response = await fetch(`${MCP_BASE_URL}/token/list`, {
        headers: {
          'Authorization': `Bearer ${storedToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTokens(data.tokens || []);
      }
    } catch (error) {
      console.error('Error loading tokens:', error);
    } finally {
      setTokensLoading(false);
    }
  };

  // Load JDs for a specific token
  const loadTokenJDs = async (token) => {
    setTokenJDsLoading(true);
    try {
      const response = await fetch(`${MCP_BASE_URL}/token/allowed-jds`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTokenJDs(data.allowed_jds || []);
      }
    } catch (error) {
      console.error('Error loading token JDs:', error);
      setTokenJDs([]);
    } finally {
      setTokenJDsLoading(false);
    }
  };

  // Create a new token
  const handleCreateToken = async () => {
    if (!companyEmail) {
      setMessage({ type: 'error', text: 'Company email not found. Please check your profile settings.' });
      return;
    }

    if (selectedJDsForToken.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one Job Description.' });
      return;
    }

    setCreatingToken(true);
    try {
      const response = await fetch(`${MCP_BASE_URL}/token/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: companyEmail,
          company_name: companyName || undefined,
          jd_ids: selectedJDsForToken,
          token_name: newTokenName || 'API Token',
          expires_in_days: newTokenExpiry
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to create token');
      }

      const data = await response.json();
      
      // Store as primary token if first one
      if (!localStorage.getItem('mcp_primary_token')) {
        localStorage.setItem('mcp_primary_token', data.token);
      }
      
      setCreatedToken(data);
      setNewTokenName('');
      setSelectedJDsForToken([]);
      setMessage({ type: 'success', text: 'Token created successfully!' });
      
      // Reload tokens
      loadTokens(companyEmail);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setCreatingToken(false);
    }
  };

  // Revoke a token
  const handleRevokeToken = async (tokenToRevoke) => {
    if (!window.confirm('Are you sure you want to revoke this token? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${MCP_BASE_URL}/token/revoke`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenToRevoke}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to revoke token');
      }

      setMessage({ type: 'success', text: 'Token revoked successfully' });
      loadTokens(companyEmail);
      
      if (selectedToken === tokenToRevoke) {
        setSelectedToken(null);
        setTokenJDs([]);
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  // Add JD to a token
  const handleAddJDToToken = async (jdId) => {
    if (!selectedToken) return;

    try {
      const response = await fetch(`${MCP_BASE_URL}/token/add-jd`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${selectedToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ jd_id: jdId })
      });

      if (!response.ok) {
        throw new Error('Failed to add JD access');
      }

      setMessage({ type: 'success', text: `Added access to ${jdId}` });
      loadTokenJDs(selectedToken);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  // Remove JD from a token
  const handleRemoveJDFromToken = async (jdId) => {
    if (!selectedToken) return;

    try {
      const response = await fetch(`${MCP_BASE_URL}/token/remove-jd`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${selectedToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ jd_id: jdId })
      });

      if (!response.ok) {
        throw new Error('Failed to remove JD access');
      }

      setMessage({ type: 'success', text: `Removed access to ${jdId}` });
      loadTokenJDs(selectedToken);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    }
  };

  // Copy token to clipboard
  const handleCopyToken = (token, tokenId) => {
    navigator.clipboard.writeText(token);
    setCopiedTokenId(tokenId);
    setTimeout(() => setCopiedTokenId(null), 2000);
  };

  // Toggle JD selection for new token
  const toggleJDSelection = (jdId) => {
    setSelectedJDsForToken(prev => 
      prev.includes(jdId) 
        ? prev.filter(id => id !== jdId)
        : [...prev, jdId]
    );
  };

  // Select all JDs
  const selectAllJDs = () => {
    setSelectedJDsForToken(availableJDs.map(jd => jd.id));
  };

  // Clear all JD selections
  const clearAllJDs = () => {
    setSelectedJDsForToken([]);
  };

  // Filter JDs by search term
  const filteredJDs = availableJDs.filter(jd => 
    jd.jdName?.toLowerCase().includes(jdSearchTerm.toLowerCase()) ||
    jd.id?.toLowerCase().includes(jdSearchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="mcp-modal-overlay" onClick={onClose}>
      <div className="mcp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="mcp-modal-header">
          <h2>
            <FiShield className="header-icon" />
            MCP Token Management
          </h2>
          <button className="mcp-close-btn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        {/* Service Status */}
        <div className={`mcp-service-status ${serviceHealth?.status === 'healthy' ? 'healthy' : 'error'}`}>
          <span className="status-dot"></span>
          <span>MCP Service: {serviceHealth?.status || 'Checking...'}</span>
          <button className="refresh-btn" onClick={checkServiceHealth} title="Refresh status">
            <FiRefreshCw />
          </button>
        </div>

        {/* Message Display */}
        {message.text && (
          <div className={`admin-message ${message.type}`}>
            {message.text}
            <button onClick={() => setMessage({ type: '', text: '' })}>×</button>
          </div>
        )}

        <div className="mcp-content">
          {/* Company Info Bar */}
          <div className="mcp-user-bar">
            <div className="user-info">
              <span className="user-avatar"><FiShield /></span>
              <div className="user-details">
                <span className="user-name">{companyName || 'Company Admin'}: </span>
                <span className="user-email">{companyEmail || 'No email configured'}</span>
              </div>
            </div>
          </div>

          {!companyEmail ? (
            <div className="mcp-auth-section">
              <div className="auth-info">
                <FiAlertCircle style={{ fontSize: '2rem', color: '#f59e0b', marginBottom: '12px' }} />
                <h3>Loading Company Information...</h3>
                <p>If this persists, please try closing and reopening this modal, or ensure you're logged in with a company account.</p>
                <button 
                  className="retry-btn"
                  onClick={() => {
                    const email = localStorage.getItem('CompanyEmailID');
                    if (email) {
                      setCompanyEmail(email);
                      loadTokens(email);
                    }
                  }}
                >
                  <FiRefreshCw /> Retry
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Tab Navigation */}
              <div className="mcp-tabs">
                <button 
                  className={`tab-btn ${activeTab === 'tokens' ? 'active' : ''}`}
                  onClick={() => setActiveTab('tokens')}
                >
                  <FiKey /> Create Token
                </button>
                <button 
                  className={`tab-btn ${activeTab === 'manage' ? 'active' : ''}`}
                  onClick={() => setActiveTab('manage')}
                >
                  <FiBriefcase /> Manage Tokens
                </button>
              </div>

              {/* Tab Content */}
              <div className="mcp-tab-content">
                {/* Create Token Tab */}
                {activeTab === 'tokens' && (
                  <div className="tokens-section">
                    {/* Created Token Alert */}
                    {createdToken && (
                      <div className="created-token-alert">
                        <div className="alert-header">
                          <FiAlertCircle /> Token Created Successfully!
                        </div>
                        <p>⚠️ Copy this token now. It won't be shown again!</p>
                        <div className="token-display">
                          <code>{createdToken.token}</code>
                          <button onClick={() => handleCopyToken(createdToken.token, 'new')}>
                            {copiedTokenId === 'new' ? <FiCheck /> : <FiCopy />}
                          </button>
                        </div>
                        <div className="token-info-grid">
                          <div><strong>Token ID:</strong> {createdToken.token_id}</div>
                          <div><strong>Email:</strong> {createdToken.email}</div>
                          <div><strong>JDs:</strong> {createdToken.jd_ids?.join(', ')}</div>
                          <div><strong>Expires:</strong> {createdToken.expires_at}</div>
                        </div>
                        <button className="dismiss-btn" onClick={() => setCreatedToken(null)}>
                          I've saved it
                        </button>
                      </div>
                    )}

                    {/* Create Token Form */}
                    <div className="create-token-form">
                      <h4><FiPlus /> Quick Token Generation</h4>
                      <p className="form-description">
                        Generate an API token to use SpotGPT with external MCP clients like Claude Desktop.
                        All your Job Descriptions are pre-selected for easy access.
                      </p>
                      
                      {/* Quick Create Button - Most prominent */}
                      <button 
                        className="create-btn primary-action"
                        onClick={handleCreateToken}
                        disabled={creatingToken || selectedJDsForToken.length === 0}
                      >
                        {creatingToken ? 'Creating Token...' : <><FiPlus /> Create Token Now</>}
                      </button>
                      
                      {/* Optional Customization - Collapsible */}
                      <details className="advanced-options">
                        <summary>Advanced Options</summary>
                        
                        <div className="form-group">
                          <label>Token Name (Optional)</label>
                          <input
                            type="text"
                            value={newTokenName}
                            onChange={(e) => setNewTokenName(e.target.value)}
                            placeholder="e.g., My MCP Token"
                          />
                        </div>

                        <div className="form-group">
                          <label>Expires In</label>
                          <select 
                            value={newTokenExpiry}
                            onChange={(e) => setNewTokenExpiry(Number(e.target.value))}
                          >
                            <option value={30}>30 days</option>
                            <option value={90}>90 days</option>
                            <option value={180}>180 days</option>
                            <option value={365}>1 year (default)</option>
                            <option value={730}>2 years</option>
                          </select>
                        </div>

                        <div className="form-group">
                          <label>
                            Job Descriptions Access 
                            <span className="selection-count">({selectedJDsForToken.length} of {availableJDs.length} selected)</span>
                          </label>
                          
                          {/* JD Selector Dropdown */}
                          <div className="jd-selector-container">
                            <div 
                              className="jd-selector-trigger"
                              onClick={() => setIsJDSelectorOpen(!isJDSelectorOpen)}
                            >
                              <span>
                                {selectedJDsForToken.length === 0 
                                  ? 'Select Job Descriptions...'
                                  : selectedJDsForToken.length === availableJDs.length
                                    ? 'All JDs selected'
                                    : `${selectedJDsForToken.length} JD(s) selected`
                                }
                              </span>
                              <FiChevronDown className={isJDSelectorOpen ? 'rotated' : ''} />
                            </div>

                            {isJDSelectorOpen && (
                              <div className="jd-selector-dropdown">
                                {/* Search */}
                                <div className="jd-search">
                                  <FiSearch />
                                  <input
                                    type="text"
                                    placeholder="Search JDs..."
                                    value={jdSearchTerm}
                                    onChange={(e) => setJDSearchTerm(e.target.value)}
                                  />
                                </div>

                                {/* Quick Actions */}
                                <div className="jd-quick-actions">
                                  <button onClick={selectAllJDs}>Select All</button>
                                  <button onClick={clearAllJDs}>Clear All</button>
                                </div>

                                {/* JD List */}
                                <div className="jd-list">
                                  {filteredJDs.length === 0 ? (
                                    <div className="no-jds">No Job Descriptions found</div>
                                  ) : (
                                    filteredJDs.map(jd => (
                                      <div 
                                        key={jd.id} 
                                        className={`jd-item ${selectedJDsForToken.includes(jd.id) ? 'selected' : ''}`}
                                        onClick={() => toggleJDSelection(jd.id)}
                                      >
                                        <div className="jd-checkbox">
                                          {selectedJDsForToken.includes(jd.id) && <FiCheck />}
                                        </div>
                                        <div className="jd-info">
                                          <span className="jd-name">{jd.jdName}</span>
                                          <span className="jd-id">{jd.id}</span>
                                        </div>
                                      </div>
                                    ))
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </details>
                    </div>

                    {/* Connection Info */}
                    <div className="connection-info">
                      <h4><FiLink /> MCP Client Configuration</h4>
                      <div className="info-box">
                      
                        <p><strong>MCP Transport (for Claude Desktop,Github Copilot and more...):</strong></p>
                        <code>https://phone.getscreened.in/mcp</code>
                        <p className="hint">
                          Enter the token when the assistant asks: <code>gs_xxx...</code>
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Manage Tokens Tab */}
                {activeTab === 'manage' && (
                  <div className="manage-section">
                    <div className="section-header">
                      <h4>Your API Tokens</h4>
                      <button className="refresh-btn-text" onClick={() => loadTokens(companyEmail)}>
                        <FiRefreshCw /> Refresh
                      </button>
                    </div>

                    {tokensLoading ? (
                      <div className="loading">Loading tokens...</div>
                    ) : tokens.length === 0 ? (
                      <div className="empty-state">
                        <FiKey className="empty-icon" />
                        <p>No tokens found.</p>
                        <p className="hint">Create your first token in the "Create Token" tab.</p>
                      </div>
                    ) : (
                      <div className="tokens-list">
                        {tokens.map((token) => (
                          <div 
                            key={token.token_id} 
                            className={`token-card ${selectedToken === token.token ? 'selected' : ''}`}
                          >
                            <div className="token-header">
                              <div className="token-main-info">
                                <span className="token-name">{token.token_name || 'API Token'}</span>
                                <span className={`token-status ${token.is_active ? 'active' : 'expired'}`}>
                                  {token.is_active ? 'Active' : 'Expired'}
                                </span>
                              </div>
                              <div className="token-actions">
                                <button 
                                  className="action-btn"
                                  onClick={() => {
                                    if (selectedToken === token.token) {
                                      setSelectedToken(null);
                                      setTokenJDs([]);
                                    } else {
                                      setSelectedToken(token.token);
                                      loadTokenJDs(token.token);
                                    }
                                  }}
                                >
                                  <FiBriefcase /> {selectedToken === token.token ? 'Close' : 'Manage JDs'}
                                </button>
                                <button 
                                  className="action-btn danger"
                                  onClick={() => handleRevokeToken(token.token)}
                                >
                                  <FiTrash2 /> Revoke
                                </button>
                              </div>
                            </div>
                            
                            <div className="token-meta">
                              <span>ID: {token.token_id}</span>
                              <span>Created: {new Date(token.created_at).toLocaleDateString()}</span>
                              <span>Expires: {new Date(token.expires_at).toLocaleDateString()}</span>
                              <span>JDs: {token.jd_count || token.jd_ids?.length || 0}</span>
                            </div>

                            {/* JD Management Panel */}
                            {selectedToken === token.token && (
                              <div className="jd-management-panel">
                                <h5>Job Description Access</h5>
                                
                                {tokenJDsLoading ? (
                                  <div className="loading-small">Loading JDs...</div>
                                ) : (
                                  <>
                                    {/* Current JDs */}
                                    <div className="current-jds">
                                      <h6>Current Access ({tokenJDs.length})</h6>
                                      {tokenJDs.length === 0 ? (
                                        <p className="no-jds-msg">No JDs assigned to this token.</p>
                                      ) : (
                                        <div className="jd-chips">
                                          {tokenJDs.map(jdId => {
                                            const jd = availableJDs.find(j => j.id === jdId);
                                            return (
                                              <div key={jdId} className="jd-chip">
                                                <span>{jd?.jdName || jdId}</span>
                                                <button onClick={() => handleRemoveJDFromToken(jdId)}>
                                                  <FiX />
                                                </button>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>

                                    {/* Add JDs */}
                                    <div className="add-jds">
                                      <h6>Add Access</h6>
                                      <div className="available-jds-grid">
                                        {availableJDs
                                          .filter(jd => !tokenJDs.includes(jd.id))
                                          .map(jd => (
                                            <button
                                              key={jd.id}
                                              className="add-jd-btn"
                                              onClick={() => handleAddJDToToken(jd.id)}
                                            >
                                              <FiPlus /> {jd.jdName}
                                            </button>
                                          ))}
                                        {availableJDs.filter(jd => !tokenJDs.includes(jd.id)).length === 0 && (
                                          <p className="all-assigned">All JDs are already assigned to this token.</p>
                                        )}
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Stats */}
                    <div className="stats-section">
                      <h4>Summary</h4>
                      <div className="stats-grid">
                        <div className="stat-card">
                          <span className="stat-value">{tokens.length}</span>
                          <span className="stat-label">Total Tokens</span>
                        </div>
                        <div className="stat-card">
                          <span className="stat-value">{tokens.filter(t => t.is_active).length}</span>
                          <span className="stat-label">Active Tokens</span>
                        </div>
                        <div className="stat-card">
                          <span className="stat-value">{availableJDs.length}</span>
                          <span className="stat-label">Available JDs</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MCPSettingsModal;
